import React, { Component } from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import LineItem from './LineItem';
import Model from '../../model/ShopKeeper.model';

interface IProps {
  editFreight: Function;
  editFuel: Function;
  fleetcrossModal: Function;
  model: Model;
}

const SCROLL_DOM_ID = 'lineItemsScrollableArea';

@observer
class InvoiceBuilder extends Component<IProps, {}> {
  model: Model;
  lastUpdateLineItemLength: null | number = null;

  constructor(props: IProps) {
    super(props);
    this.model = props.model;
  }

  componentDidUpdate() {
    const empty = this.model.lineItems.length === 0;

    // auto-scroll InvoiceBuilder window to bottom of lineitems on additions
    // scroll will not occur on deletions (inferred by tracking the lineItems length)
    if (
      !empty &&
      !this.model.lineItemDragInProcess &&
      this.lastUpdateLineItemLength &&
      this.model.lineItems.length > this.lastUpdateLineItemLength
    ) {
      scrollInvoiceBuilder();
    }

    this.lastUpdateLineItemLength = this.model.lineItems.length;
  }

  // dragIndex: from, hoverIndex: to
  @action moveLineItem = (dragIndex: number, hoverIndex: number) => {
    const items = this.model.lineItems;
    const temp = items[dragIndex];
    items[dragIndex] = items[hoverIndex];
    items[hoverIndex] = temp;
  };

  render() {
    const empty = this.model.lineItems.length === 0;

    let netPriceType = '';
    let netPriceTypeTitle = '';

    switch (this.model.sysParams.partsInquiryDisplayOptions) {
      case 'T2':
        netPriceType = 'TRADE2';
        netPriceTypeTitle = 'TRADE2 PRICE';
        break;
      case 'T3':
        netPriceType = 'TRADE3';
        netPriceTypeTitle = 'TRADE3 PRICE';
        break;
      default:
        netPriceType = 'NET';
        netPriceTypeTitle = 'NET PRICE';
        break;
    }

    return (
      <DndProvider backend={HTML5Backend}>
        <div className={`sk-mainInvoice${empty ? ' sk-mainInvoiceEmpty' : ''}`}>
          {empty ? (
            'DSI Point of Sale'
          ) : (
            <div className="sk-lineItemsContainer sk-modal-container">
              <div className="sk-lineItemsStickyHeader">
                <table className="sk-lineItemCols sk-lineItemColsHeaders">
                  <tbody>
                    <tr>
                      <td>
                        <span title="QUANTITY">QTY</span>
                      </td>
                      <td>
                        <span title="PART NUMBER">PART</span>
                      </td>
                      <td>
                        <span title="ON HAND">OH</span>
                      </td>
                      <td>
                        <span title="DESCRIPTION">DESCRIPTION</span>
                      </td>
                      <td>
                        <span title="BIN LOCATIONS">LOCATION</span>
                      </td>
                      <td>
                        <span title="STOCK CLASS">STCL</span>
                      </td>
                      <td>
                        <span title="PRICE SYMBOL">PSY</span>
                      </td>
                      <td>
                        <span title={netPriceTypeTitle}>{netPriceType}</span>
                      </td>
                      <td>
                        <span title="LIST PRICE">LIST</span>
                      </td>
                      <td>
                        <span title="SELL PRICE">SELL</span>
                      </td>
                      <td>
                        {this.model.sysParams.displayGrossProfitOnInquiry ? <span title="GROSS PROFIT %">GP</span> : ''}
                      </td>
                      <td>&nbsp;</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {['PIN1', 'PIN4', 'PSO3'].includes(this.model.runMode) && (
                <div id={SCROLL_DOM_ID} className="sk-lineItems">
                  {/* Active line items (possibly in a fetching status) */}
                  {this.model.lineItems.map((lineItem, index) => (
                    <LineItem
                      key={index}
                      itemIndex={index}
                      fleetcrossModal={this.props.fleetcrossModal}
                      lineItem={lineItem}
                      model={this.model}
                      moveLineItem={this.moveLineItem}
                    />
                  ))}
                  {/* Queued line items (pending fetch/eval) */}
                  {this.model.lineItemsQueued.map((lineItem, index) => (
                    <div key={index} className="sk-queued">
                      {lineItem} - Queued ...
                    </div>
                  ))}

                  {this.model.runMode === 'PIN1' && this.model.activeCustomerID !== null && (
                    <>
                      {/* If the user has set a freight on this invoice trough the UI control, show it as a line item. */}
                      {this.model.freight !== null && (
                        <div className="sk-lineItemColsHoverable">
                          <table className="sk-lineItemCols">
                            <tbody>
                              <tr>
                                <td />
                                <td>FREIGHT</td>
                                <td />
                                <td>FREIGHT</td>
                                <td />
                                <td />
                                <td />
                                <td />
                                <td />
                                <td className="sk-editField" onClick={() => this.props.editFreight()}>
                                  {this.model.freight.toFixed(2)}
                                </td>
                                <td />
                                <td>
                                  <span
                                    className="sk-removeItemIcon"
                                    onClick={() => this.props.editFreight()}
                                    title="Remove Line Item"
                                  >
                                    <i className="zmdi zmdi-delete zmdi-hc-lg" />
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                      {/* If the user has set a fuel charge on this invoice trough the UI control, show it as a line item. */}
                      {this.model.fuelCharge !== null && (
                        <div className="sk-lineItemColsHoverable">
                          <table className="sk-lineItemCols">
                            <tbody>
                              <tr>
                                <td />
                                <td>{this.model.sysParams.fuelChargeDefaultPartName}</td>
                                <td />
                                <td>FUEL DELIVERY</td>
                                <td />
                                <td />
                                <td />
                                <td />
                                <td />
                                <td className="sk-editField" onClick={() => this.props.editFuel()}>
                                  {this.model.fuelCharge.toFixed(2)}
                                </td>
                                <td />
                                <td>
                                  <span
                                    className="sk-removeItemIcon"
                                    onClick={() => this.props.editFuel()}
                                    title="Remove Line Item"
                                  >
                                    <i className="zmdi zmdi-delete zmdi-hc-lg" />
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {['PRO1', 'SMM3', 'SRE2'].includes(this.model.runMode) && (
                <div id={SCROLL_DOM_ID} className="sk-lineItems">
                  {/* Active line items (possibly in a fetching status) */}
                  {/* The Active operation filter is applied by the LineItem component directly in order to maintain position logic here. */}
                  {this.model.lineItems.map((lineItem, index) => (
                    <div
                      key={index}
                      onClickCapture={(e: any) => {
                        // Move to operation selection hook
                        if (this.model.moveOperationsModeActive) {
                          // Attempting a split quantity edit? (see LineItem.tsx:renderEditableQuantityCell())
                          if (e.target.classList.contains('sk-moveOpSplitQtyEligible')) {
                            // Line already selected. Allow propogation for qty edit if conditions met.
                          } else {
                            this.model.moveOperationsLineItemClicked(index);
                            e.stopPropagation();
                          }
                        }
                      }}
                      className={this.model.moveOperationsModeActive ? 'sk-moveOperationsModeActive' : undefined}
                    >
                      <LineItem
                        itemIndex={index}
                        fleetcrossModal={this.props.fleetcrossModal}
                        lineItem={lineItem}
                        model={this.model}
                        moveLineItem={this.moveLineItem}
                      />
                    </div>
                  ))}
                  {/* Queued line items (pending fetch/eval) */}
                  {this.model.lineItemsQueued.map((lineItem, index) => (
                    <div key={index} className="sk-queued">
                      {lineItem} - Queued ...
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </DndProvider>
    );
  }
}

export function scrollInvoiceBuilder() {
  // Better results when scrolling on the next tick
  setTimeout(() => {
    const el = document.getElementById(SCROLL_DOM_ID) as HTMLElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, 0);
}

export default InvoiceBuilder;
